import React, { useState } from 'react';
import { Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useTranslation } from '../context/TranslationContext';
import {
  createAdditionalItem,
  getFilledAdditionalItems,
  parseAdditionalItemPrice,
} from '../lib/additionalItems';
import ConfiguratorDrawingPriceAdjustment from './ConfiguratorDrawingPriceAdjustment';

const ConfiguratorAdditionalItems = ({
  items = [],
  onItemsChange,
  config,
  onConfigChange,
}) => {
  const { t } = useTranslation();
  const filledCount = getFilledAdditionalItems(items).length;
  const [expanded, setExpanded] = useState(filledCount > 0);
  const [priceDrafts, setPriceDrafts] = useState({});

  const updateItems = (updater) => {
    onItemsChange((current) => updater(current || items));
  };

  const updateItem = (id, field, value) => {
    updateItems((current) =>
      current.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const commitPrice = (id, rawValue) => {
    const parsed = parseAdditionalItemPrice(rawValue);
    updateItem(id, 'price', parsed);
    setPriceDrafts((drafts) => {
      const next = { ...drafts };
      delete next[id];
      return next;
    });
  };

  const getPriceDisplay = (item) => {
    if (Object.prototype.hasOwnProperty.call(priceDrafts, item.id)) {
      return priceDrafts[item.id];
    }
    return item.price === 0 ? '' : String(item.price);
  };

  const addItem = () => {
    updateItems((current) => [...current, createAdditionalItem(current.length)]);
    setExpanded(true);
  };

  const removeItem = (id) => {
    if (items.length <= 1) return;
    updateItems((current) => current.filter((item) => item.id !== id));
    setPriceDrafts((drafts) => {
      const next = { ...drafts };
      delete next[id];
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="w-full flex items-start justify-between gap-3 text-left rounded-md -m-1 p-1 hover:bg-gray-50 transition-colors"
            aria-expanded={expanded}
          >
            <div className="min-w-0">
              <CardTitle className="text-base">{t('options.additionalItems')}</CardTitle>
              <CardDescription className="text-xs mt-1">
                {expanded
                  ? t('options.additionalItemsDesc')
                  : filledCount > 0
                    ? t('options.additionalItemsCollapsedCount', { count: filledCount })
                    : t('options.additionalItemsCollapsedHint')}
              </CardDescription>
            </div>
            <span className="shrink-0 mt-0.5 text-gray-500" aria-hidden>
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </span>
          </button>
        </CardHeader>

        {expanded ? (
          <CardContent className="space-y-2 pt-0">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                  placeholder={t('options.additionalItemName')}
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <div className="relative w-24 shrink-0">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-500">€</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={getPriceDisplay(item)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setPriceDrafts((drafts) => ({ ...drafts, [item.id]: raw }));
                      updateItem(item.id, 'price', parseAdditionalItemPrice(raw));
                    }}
                    onBlur={(e) => commitPrice(item.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    placeholder="0"
                    className="w-full pl-7 pr-2 py-2 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                {items.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    aria-label={t('options.removeAdditionalItem')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="w-8 shrink-0" aria-hidden />
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('options.addAdditionalItem')}
            </button>
          </CardContent>
        ) : null}
      </Card>

      {onConfigChange ? (
        <ConfiguratorDrawingPriceAdjustment
          config={config}
          onConfigChange={onConfigChange}
        />
      ) : null}
    </div>
  );
};

export default ConfiguratorAdditionalItems;
